/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useCallback, useEffect, useRef } from "react";

import type { InputInterceptor } from "../../model/navigation";
import { Log } from "../../model/logging";
import { UnityAppStore, useUnityDispatch } from "../../viewModel";
import { hideOsd, showOsd } from "../../viewModel/osd";
import { useNavigationEngine } from "./useNavigationEngine";

const TAG: string = "useInactivityOsd";

/** Base name used to register this hook's input interceptor on the engine. */
const INTERCEPTOR_NAME_PREFIX: string = "inactivityOsd";

/** Ensures concurrent hook instances (e.g. two screens mounted at once) never register the same interceptor name. */
let interceptorInstanceCounter: number = 0;

export interface UseInactivityOsdParams {
  /** Whether the inactivity timer should be running (e.g. only while playing). */
  enabled: boolean;

  /** Idle time (ms) before the OSD is shown. */
  timeoutMs: number;

  /** Message shown in the OSD. */
  message: string;

  /** Short code shown alongside the message (empty string if none). */
  code: string;

  /** Priority passed to the shared `osd` slice's priority-guard. */
  priority: number;

  /**
   * Whether the OSD should periodically reposition itself to prevent
   * screen burn-in while shown. Passed straight through to the shared
   * `osd` slice's `isRepositionRequired` flag — the screen decides whether
   * its inactivity OSD needs this (e.g. not needed for a short-lived,
   * auto-exiting message).
   */
  isRepositionRequired: boolean;

  /**
   * Idle time (ms), measured from when the OSD is first shown, after which
   * `onExit` is invoked. Omit to keep the OSD showing indefinitely until the
   * next input (no auto-exit stage).
   */
  exitTimeoutMs?: number;

  /**
   * Called when `exitTimeoutMs` elapses with no input after the OSD is
   * shown. The screen decides what "exit" means (e.g. navigate to the
   * previous screen). If omitted, the OSD is simply hidden and the
   * inactivity cycle restarts.
   */
  onExit?: () => void;
}

/**
 * Reusable inactivity "Still There" OSD hook.
 *
 * Centralizes the *mechanics* — the idle timer and "reset on any input"
 * wiring — while each screen retains full ownership of *what* is shown by
 * supplying its own `timeoutMs`, `message`, `priority`, and optional
 * `exitTimeoutMs`/`onExit`. Screens that don't need an auto-exit stage
 * simply omit `exitTimeoutMs`/`onExit`.
 *
 * "Any input" is detected via a single {@link NavigationEngine} interceptor,
 * so the same reset logic covers both TV remote/keyboard input and browser
 * pointer input (click/wheel/mouseover/etc.) — the engine already
 * normalizes both into the same event pipeline ahead of screen routing.
 *
 * Timers live in refs (never in Redux — they are not serializable) and are
 * cleared on every input, whenever `enabled` becomes `false`, and on
 * unmount.
 *
 * The `osd` slice is a single slot shared with unrelated features, so this hook
 * never clobbers a message it does not own: it only calls `hideOsd()` when its
 * own `showOsd()` won the slice's priority guard AND it still holds the slot at
 * hide time. Both checks matter — a higher-priority message can take the slot
 * at any point after we won it (see {@link isOsdStillOurs}).
 */
export function useInactivityOsd(params: UseInactivityOsdParams): void {
  const dispatch = useUnityDispatch();
  const engine = useNavigationEngine();

  // Mirror the latest params into a ref so the timer/interceptor callbacks
  // (registered once) always read current values without needing to
  // re-register on every render.
  const paramsRef = useRef<UseInactivityOsdParams>(params);
  useEffect((): void => {
    paramsRef.current = params;
  }, [params]);

  const primaryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tracks whether the OSD currently on screen was shown by THIS hook
  // instance, so a later hide never clobbers a higher-priority message from
  // an unrelated feature that won the shared slice's guard instead of us.
  const isShownByUsRef = useRef<boolean>(false);

  // Holds the latest `startPrimaryTimer` so the exit-timer callback can loop
  // back into it without referencing the `const` by name inside its own
  // body (self-recursive `useCallback` consts trip `no-use-before-define`).
  const startPrimaryTimerRef = useRef<() => void>(() => {});

  const clearPrimaryTimer = useCallback((): void => {
    if (primaryTimerRef.current !== null) {
      clearTimeout(primaryTimerRef.current);
      primaryTimerRef.current = null;
    }
  }, []);

  const clearExitTimer = useCallback((): void => {
    if (exitTimerRef.current !== null) {
      clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }
  }, []);

  /**
   * Whether the message currently held by the shared `osd` slice is the one
   * this hook put there.
   *
   * Compares against live store state rather than trusting a remembered flag:
   * the slice is a single slot, so a higher-priority message (e.g. the
   * network-error OSD at 999) can legitimately take it over at any moment after
   * we won it. Checked by message AND priority, since another feature could
   * reuse either one alone.
   */
  const isOsdStillOurs = useCallback((): boolean => {
    const osdState = UnityAppStore.getState().osd;
    const current = paramsRef.current;
    return (
      osdState.message === current.message &&
      osdState.priority === current.priority
    );
  }, []);

  /**
   * Hide the OSD, but only while we still own it.
   *
   * Re-checks ownership at hide time. Without that, losing the slot to a
   * higher-priority message left `isShownByUsRef` stale, and the next hide from
   * any source — input, `enabled` flipping false, the exit timer, unmount —
   * dispatched `hideOsd()` and wiped the message that had legitimately
   * outranked us. Symptom: disconnecting the network while the inactivity OSD
   * was up showed no network OSD at all, because playback stopping flipped
   * `enabled` and the hook immediately hid it.
   *
   * Ownership is always surrendered, whether or not we hid anything — once
   * another message holds the slot, this hook has no further claim on it.
   */
  const hideIfOwnedByUs = useCallback((): void => {
    if (!isShownByUsRef.current) {
      return;
    }
    const stillOurs: boolean = isOsdStillOurs();
    isShownByUsRef.current = false;
    if (!stillOurs) {
      Log.d(
        TAG,
        "hideIfOwnedByUs() — skipped, a higher-priority message owns the OSD"
      );
      return;
    }
    dispatch(hideOsd());
  }, [dispatch, isOsdStillOurs]);

  const startPrimaryTimer = useCallback((): void => {
    clearPrimaryTimer();
    if (!paramsRef.current.enabled) {
      return;
    }

    primaryTimerRef.current = setTimeout((): void => {
      primaryTimerRef.current = null;
      const current = paramsRef.current;

      dispatch(
        showOsd({
          message: current.message,
          code: current.code,
          priority: current.priority,
          isRepositionRequired: current.isRepositionRequired
        })
      );

      isShownByUsRef.current = isOsdStillOurs();

      if (!isShownByUsRef.current) {
        Log.i(
          TAG,
          "startPrimaryTimer() — showOsd() was blocked by a higher-priority message"
        );
        return;
      }

      Log.d(TAG, "startPrimaryTimer() — inactivity OSD shown");

      if (current.exitTimeoutMs !== undefined) {
        exitTimerRef.current = setTimeout((): void => {
          exitTimerRef.current = null;
          Log.i(TAG, "exitTimer fired — no input after OSD was shown");
          hideIfOwnedByUs();

          if (current.onExit !== undefined) {
            current.onExit();
          } else {
            startPrimaryTimerRef.current();
          }
        }, current.exitTimeoutMs);
      }
    }, paramsRef.current.timeoutMs);
  }, [clearPrimaryTimer, dispatch, hideIfOwnedByUs, isOsdStillOurs]);

  useEffect((): void => {
    startPrimaryTimerRef.current = startPrimaryTimer;
  }, [startPrimaryTimer]);

  const resetOnActivity = useCallback((): void => {
    if (!paramsRef.current.enabled) {
      return;
    }
    clearExitTimer();
    hideIfOwnedByUs();
    startPrimaryTimer();
  }, [clearExitTimer, hideIfOwnedByUs, startPrimaryTimer]);

  // Register a single input interceptor for the lifetime of this hook
  // instance. Deps are all stable (refs/useCallback with stable deps), so
  // this effect runs once on mount and its cleanup only runs on real
  // unmount — not on every param change.
  useEffect((): (() => void) => {
    interceptorInstanceCounter += 1;
    const interceptorName = `${INTERCEPTOR_NAME_PREFIX}-${interceptorInstanceCounter}`;

    const interceptor: InputInterceptor = (): void => {
      resetOnActivity();
    };

    engine.getInterceptors().use(interceptorName, interceptor);
    Log.d(TAG, `registered interceptor "${interceptorName}"`);

    return (): void => {
      engine.getInterceptors().remove(interceptorName);
      clearPrimaryTimer();
      clearExitTimer();
      hideIfOwnedByUs();
      Log.d(TAG, `removed interceptor "${interceptorName}" on unmount`);
    };
  }, [
    engine,
    resetOnActivity,
    clearPrimaryTimer,
    clearExitTimer,
    hideIfOwnedByUs
  ]);

  // Start/stop the idle timer whenever `enabled` transitions. The effect is
  // keyed on the `enabled` primitive (not the whole `params` object, whose
  // identity changes every render) so it only re-runs on a real transition.
  // The callback deps below are all referentially stable across renders
  // (useCallback with stable deps), so listing them satisfies exhaustive-deps
  // without causing extra re-runs.
  useEffect((): void => {
    if (params.enabled) {
      startPrimaryTimer();
    } else {
      clearPrimaryTimer();
      clearExitTimer();
      hideIfOwnedByUs();
    }
  }, [
    params.enabled,
    startPrimaryTimer,
    clearPrimaryTimer,
    clearExitTimer,
    hideIfOwnedByUs
  ]);
}
